import React from 'react';
import dayjs from 'dayjs';
import { Timeline, Card } from 'antd';
import { CustomerChangeRequest } from '../../services/customers';

interface TimelineTabProps {
  changeRequests: CustomerChangeRequest[];
}

export const TimelineTab: React.FC<TimelineTabProps> = ({
  changeRequests,
}) => {
  if (changeRequests.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
        No change requests found
      </div>
    );
  }

  const getFieldLabel = (fieldName: string): string => {
    // Handle family fields
    if (fieldName.startsWith('family_')) {
      const match = fieldName.match(/^family_(spouse|children)_(\d+)_(name|identification_no)$/);
      if (match) {
        const [, type, index, field] = match;
        const typeLabel = type === 'spouse' ? 'Spouse' : 'Children';
        const fieldLabel = field === 'name' ? 'Name' : 'Identification No.';
        return `${typeLabel} ${parseInt(index) + 1} - ${fieldLabel}`;
      }
    }
    // Handle asset fields
    if (fieldName.startsWith('asset_')) {
      const match = fieldName.match(/^asset_(planning|existing)_(\d+)_(name|year|price|installment)$/);
      if (match) {
        const [, type, index, field] = match;
        const typeLabel = type === 'planning' ? 'Planning' : 'Existing';
        const fieldLabels: Record<string, string> = {
          name: type === 'planning' ? 'Property' : 'Car Model',
          year: 'Year',
          price: 'Price',
          installment: 'Installment',
        };
        return `${typeLabel} ${parseInt(index) + 1} - ${fieldLabels[field] || field}`;
      }
    }
    // Handle regular customer fields
    const fieldLabels: Record<string, string> = {
      name: 'Full Name',
      ic_passport_no: 'IC / Passport No',
      tin_no: 'TIN No.',
      nationality: 'Nationality',
      race: 'Race',
      sex: 'Sex',
    };
    return fieldLabels[fieldName] || fieldName;
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '';
    return dayjs(dateString).format('DD/MM/YYYY HH:mm');
  };

  const formatValue = (fieldName: string, value?: string): string => {
    if (!value) return '';
    
    // Format sex field
    if (fieldName === 'sex') {
      const sexMap: Record<string, string> = {
        'male': 'Male',
        'female': 'Female',
        'other': 'Other',
        'm': 'Male',
        'f': 'Female',
      };
      return sexMap[value.toLowerCase()] || value;
    }
    
    return value;
  };

  const getActionText = (request: CustomerChangeRequest): string => {
    const fieldLabel = getFieldLabel(request.field_name).toLowerCase();
    
    if (request.status === 'approved') {
      const reviewerName = request.reviewer?.name || 'Admin';
      return `${reviewerName} update the ${fieldLabel}`;
    } else if (request.status === 'rejected') {
      const reviewerName = request.reviewer?.name || 'Admin';
      return `${reviewerName} reject change in ${fieldLabel}`;
    } else {
      // Pending status: show "User request change in [field] from [old_value] to [new_value]"
      const oldValue = formatValue(request.field_name, request.old_value || '');
      const newValue = formatValue(request.field_name, request.new_value);
      
      if (oldValue) {
        return `User request change in ${fieldLabel} from ${oldValue} to ${newValue}`;
      } else {
        return `User request change in ${fieldLabel} to ${newValue}`;
      }
    }
  };

  const getTimestamp = (request: CustomerChangeRequest): string => {
    if (request.status === 'approved' && request.reviewed_at) {
      return formatDate(request.reviewed_at);
    } else if (request.status === 'rejected' && request.reviewed_at) {
      return formatDate(request.reviewed_at);
    } else {
      return formatDate(request.requested_at || request.created_at);
    }
  };

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 20px', position: 'relative' }}>
      {/* Timeline line */}
      <div style={{
        position: 'absolute',
        left: '50%',
        top: 0,
        bottom: 0,
        width: '2px',
        backgroundColor: '#d9d9d9',
        transform: 'translateX(-50%)',
      }} />
      
      <div style={{ position: 'relative' }}>
        {changeRequests.map((request, index) => {
          // Determine left/right based on timestamps:
          // - requested_at has value: left (green) - user requested
          // - reviewed_at has value: right (blue) - admin reviewed
          const hasRequestedAt = request.requested_at !== null && request.requested_at !== undefined;
          const hasReviewedAt = request.reviewed_at !== null && request.reviewed_at !== undefined;
          
          // If reviewed_at exists, always show on right side
          // Otherwise, if requested_at exists, show on left side
          // If both exist, prioritize reviewed_at (right side)
          const displayLeft = hasRequestedAt && !hasReviewedAt;
          
          // Debug logging
          console.log(`[Timeline ${index}]`, {
            id: request.id,
            field_name: request.field_name,
            requested_at: request.requested_at,
            reviewed_at: request.reviewed_at,
            hasRequestedAt,
            hasReviewedAt,
            displayLeft,
            side: displayLeft ? 'LEFT' : 'RIGHT'
          });
          
          const cardColor = displayLeft ? '#f6ffed' : '#e6f7ff'; // Light green for left, light blue for right
          const nodeColor = displayLeft ? 'green' : 'blue';

          return (
            <div
              key={request.id}
              style={{
                position: 'relative',
                marginBottom: 24,
                minHeight: 60,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {/* Timeline node */}
              <div style={{
                position: 'absolute',
                left: '50%',
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: nodeColor,
                border: '2px solid white',
                transform: 'translateX(-50%)',
                zIndex: 1,
              }} />
              
              {/* Card */}
              <Card
                style={{
                  width: 300,
                  backgroundColor: cardColor,
                  marginLeft: displayLeft ? 'calc(50% - 320px)' : 'calc(50% + 20px)',
                  marginRight: 0,
                  textAlign: 'left',
                }}
              >
                <div style={{ fontWeight: 500, marginBottom: 8, textAlign: 'left' }}>
                  {getActionText(request)}
                </div>
                <div style={{ color: '#999', fontSize: '12px', textAlign: 'left' }}>
                  Last update: {getTimestamp(request)}
                </div>
              </Card>
            </div>
          );
        })}
      </div>
    </div>
  );
};
